import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Card,
  Select,
  Table,
  Tag,
  Space,
  Typography,
  Tabs,
  message,
  Spin,
  Collapse,
} from 'antd';
import { listAllPermissions, listResources, upsertPermission, deletePermission, getLookups } from '../../api';
import PersonAccess from '../../components/admin/PersonAccess';

const { Title, Text } = Typography;

interface ResourceItem {
  id: number;
  key: string;
  label: string;
  module: string;
  sortOrder: number;
}

interface PermissionRow {
  id: number;
  resource: string;
  accessLevel: string;
  grantType: string;
  grantValue: string;
}

const ACCESS_LEVELS = [
  { label: 'Full', value: 'full', color: 'green' },
  { label: 'Create', value: 'create', color: 'blue' },
  { label: 'Read Only', value: 'read_only', color: 'orange' },
  { label: 'None', value: 'none', color: 'red' },
];

const ROLES = [
  { label: 'Approver (CEO)', value: 'approver' },
  { label: 'Procurement Officer', value: 'procurement_officer' },
  { label: 'Requester', value: 'requester' },
];

const accessColor = (level: string) => {
  const found = ACCESS_LEVELS.find(a => a.value === level);
  return found?.color || 'default';
};

const AdminPermissions: React.FC = () => {
  const [resources, setResources] = useState<ResourceItem[]>([]);
  const [permissions, setPermissions] = useState<PermissionRow[]>([]);
  const [departments, setDepartments] = useState<{ id: number; name: string; code: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  // Current selections
  const [activeTab, setActiveTab] = useState<string>('by-role');
  const [selectedRole, setSelectedRole] = useState<string>('approver');
  const [selectedDept, setSelectedDept] = useState<string>('');

  // Pending changes: key = "resource|grantType|grantValue", value = new accessLevel or '__inherit'
  const [pendingChanges, setPendingChanges] = useState<Map<string, string>>(new Map());

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [res, perms, lookups] = await Promise.all([
        listResources(),
        listAllPermissions(),
        getLookups(),
      ]);
      setResources(res);
      setPermissions(perms);
      setDepartments(lookups.departments || []);
    } catch (err: any) {
      message.error('Failed to load permissions data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Group resources by module
  const modules = resources.reduce<Record<string, ResourceItem[]>>((acc, r) => {
    if (!acc[r.module]) acc[r.module] = [];
    acc[r.module].push(r);
    return acc;
  }, {});

  // Get SAVED access level from DB data
  const getSavedAccess = (resource: string, grantType: string, grantValue: string): string | null => {
    const perm = permissions.find(
      p => p.resource === resource && p.grantType === grantType && p.grantValue === grantValue
    );
    return perm?.accessLevel || null;
  };

  // Get DISPLAY access level (pending change first, then saved)
  const getDisplayAccess = (resource: string, grantType: string, grantValue: string): string | null => {
    const key = `${resource}|${grantType}|${grantValue}`;
    if (pendingChanges.has(key)) {
      const val = pendingChanges.get(key)!;
      return val === '__inherit' ? null : val;
    }
    return getSavedAccess(resource, grantType, grantValue);
  };

  // Check if a specific permission has a pending change
  const isChanged = (resource: string, grantType: string, grantValue: string): boolean => {
    return pendingChanges.has(`${resource}|${grantType}|${grantValue}`);
  };

  // Handle dropdown change — local only, no API call
  const handleAccessChange = (
    resource: string,
    accessLevel: string,
    grantType: string,
    grantValue: string
  ) => {
    const key = `${resource}|${grantType}|${grantValue}`;
    setPendingChanges(prev => {
      const next = new Map(prev);
      const savedAccess = getSavedAccess(resource, grantType, grantValue);

      // If setting back to original value, remove from pending
      if (accessLevel === '__inherit' && !savedAccess) {
        next.delete(key);
      } else if (accessLevel === savedAccess) {
        next.delete(key);
      } else {
        next.set(key, accessLevel);
      }
      return next;
    });
  };

  // Apply all pending changes
  const handleApply = async () => {
    setSaving('batch');
    try {
      let successCount = 0;
      for (const [key, accessLevel] of Array.from(pendingChanges.entries())) {
        const [resource, grantType, grantValue] = key.split('|');
        if (accessLevel === '__inherit') {
          // Delete the row to let it inherit from module
          const existing = permissions.find(
            p => p.resource === resource && p.grantType === grantType && p.grantValue === grantValue
          );
          if (existing) {
            await deletePermission(existing.id);
          }
        } else {
          await upsertPermission({ resource, accessLevel, grantType, grantValue });
        }
        successCount++;
      }
      message.success(`${successCount} permission(s) updated`);
      setPendingChanges(new Map());
      await loadAll();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to save permissions');
    } finally {
      setSaving(null);
    }
  };

  // Discard all pending changes
  const handleDiscard = () => {
    setPendingChanges(new Map());
  };

  // Render the permission matrix for a given grantType and grantValue
  const renderMatrix = (grantType: string, grantValue: string) => {
    if (!grantValue) return <Text type="secondary">Select a {grantType} to configure permissions.</Text>;

    const moduleNames = Object.keys(modules).sort((a, b) => {
      const aOrder = modules[a]?.[0]?.sortOrder || 0;
      const bOrder = modules[b]?.[0]?.sortOrder || 0;
      return aOrder - bOrder;
    });

    const collapseItems = moduleNames.map(mod => {
      const moduleResources = modules[mod];
      const moduleResource = moduleResources.find(r => r.key === mod);
      const pageResources = moduleResources.filter(r => r.key !== mod && r.key.includes('.'));

      const moduleAccess = getDisplayAccess(mod, grantType, grantValue);
      const moduleChanged = isChanged(mod, grantType, grantValue);

      return {
        key: mod,
        label: (
          <Space>
            <Text strong style={{ textTransform: 'capitalize', fontSize: 14 }}>
              {moduleResource?.label || mod}
            </Text>
            {moduleAccess ? (
              <Tag color={accessColor(moduleAccess)} style={{ fontSize: 11 }}>
                {moduleAccess.replace(/_/g, ' ').toUpperCase()}
              </Tag>
            ) : (
              <Tag color="default" style={{ fontSize: 11 }}>NO ACCESS</Tag>
            )}
            {moduleChanged && <Tag color="gold" style={{ fontSize: 10 }}>MODIFIED</Tag>}
          </Space>
        ),
        children: (
          <div>
            {/* Module-level access */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
              padding: '8px 12px', borderRadius: 6,
              background: moduleChanged ? '#fffbe6' : '#fafafa',
              border: moduleChanged ? '1px solid #ffe58f' : '1px solid transparent',
            }}>
              <Text strong style={{ width: 200 }}>Entire Module</Text>
              <Select
                value={moduleAccess || 'none'}
                style={{ width: 160 }}
                onChange={(val) => handleAccessChange(mod, val, grantType, grantValue)}
                options={ACCESS_LEVELS.map(a => ({
                  label: <Tag color={a.color} style={{ margin: 0 }}>{a.label}</Tag>,
                  value: a.value,
                }))}
              />
              {moduleChanged && <Tag color="gold" style={{ fontSize: 10 }}>unsaved</Tag>}
            </div>

            {/* Page-level overrides */}
            {pageResources.length > 0 && (
              <Table
                size="small"
                pagination={false}
                dataSource={pageResources}
                rowKey="key"
                columns={[
                  {
                    title: 'Page',
                    dataIndex: 'label',
                    width: 200,
                    render: (label: string) => <Text>{label}</Text>,
                  },
                  {
                    title: 'Access Level',
                    width: 220,
                    render: (_: any, record: ResourceItem) => {
                      const pageAccess = getDisplayAccess(record.key, grantType, grantValue);
                      const changed = isChanged(record.key, grantType, grantValue);

                      return (
                        <Space>
                          <Select
                            value={pageAccess || '__inherit'}
                            style={{
                              width: 160,
                              ...(changed ? { background: '#fffbe6', borderRadius: 6 } : {}),
                            }}
                            onChange={(val) => handleAccessChange(record.key, val, grantType, grantValue)}
                            options={[
                              {
                                label: <Tag color="default" style={{ margin: 0 }}>Inherit</Tag>,
                                value: '__inherit',
                              },
                              ...ACCESS_LEVELS.map(a => ({
                                label: <Tag color={a.color} style={{ margin: 0 }}>{a.label}</Tag>,
                                value: a.value,
                              })),
                            ]}
                          />
                          {changed && <Tag color="gold" style={{ fontSize: 10 }}>unsaved</Tag>}
                        </Space>
                      );
                    },
                  },
                  {
                    title: 'Effective',
                    width: 140,
                    render: (_: any, record: ResourceItem) => {
                      const pageAccess = getDisplayAccess(record.key, grantType, grantValue);
                      const effectiveAccess = pageAccess || moduleAccess || 'none';
                      const isInherited = !pageAccess;

                      return (
                        <Tag color={accessColor(effectiveAccess)}>
                          {effectiveAccess.replace(/_/g, ' ').toUpperCase()}
                          {isInherited && ' (inherited)'}
                        </Tag>
                      );
                    },
                  },
                ]}
                rowClassName={(record) =>
                  isChanged(record.key, grantType, grantValue) ? 'permission-row-changed' : ''
                }
              />
            )}
          </div>
        ),
      };
    });

    return (
      <Collapse
        defaultActiveKey={moduleNames}
        items={collapseItems}
        style={{ background: '#fff' }}
      />
    );
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>;
  }

  return (
    <div style={{ paddingBottom: pendingChanges.size > 0 ? 64 : 0 }}>
      <Title level={5} style={{ marginBottom: 16 }}>Resource Permissions</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Configure which roles and departments can access each module and page. Module-level sets the default; page-level overrides inherit from the module unless explicitly set.
      </Text>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'by-role',
            label: 'By Role',
            children: (
              <div>
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Space>
                    <Text strong>Role:</Text>
                    <Select
                      value={selectedRole}
                      style={{ width: 250 }}
                      onChange={setSelectedRole}
                      options={ROLES}
                    />
                  </Space>
                </Card>
                {renderMatrix('role', selectedRole)}
              </div>
            ),
          },
          {
            key: 'by-department',
            label: 'By Department',
            children: (
              <div>
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Space>
                    <Text strong>Department:</Text>
                    <Select
                      value={selectedDept}
                      style={{ width: 250 }}
                      onChange={setSelectedDept}
                      placeholder="Select department"
                      options={departments.map(d => ({ label: `${d.name} (${d.code})`, value: d.code }))}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </Space>
                </Card>
                {renderMatrix('department', selectedDept)}
              </div>
            ),
          },
          // Step 5f (item 17): a grant to one person, on top of their role and department.
          { key: 'by-person', label: 'By Person', children: <PersonAccess resourceLabel={(k) => resources.find((r) => r.key === k)?.label || k} /> },
        ]}
      />

      {/* Floating action bar for pending changes */}
      {pendingChanges.size > 0 && (
        <div style={{
          position: 'sticky',
          bottom: 0,
          background: '#fff',
          borderTop: '2px solid #c7003a',
          padding: '12px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 10,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
          borderRadius: '8px 8px 0 0',
          marginLeft: -24,
          marginRight: -24,
        }}>
          <Space>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#faad14', display: 'inline-block' }} />
            <Text strong style={{ fontSize: 14 }}>
              {pendingChanges.size} unsaved change{pendingChanges.size > 1 ? 's' : ''}
            </Text>
          </Space>
          <Space>
            <Button onClick={handleDiscard} disabled={saving === 'batch'}>
              Discard
            </Button>
            <Button
              type="primary"
              onClick={handleApply}
              loading={saving === 'batch'}
              style={{ background: '#c7003a', borderColor: '#c7003a' }}
            >
              Apply Changes
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
};

export default AdminPermissions;
